import {
    useLayoutEffect,
    useState
} from "react";
import {
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    PageContainer,
    CodeViewer,
    SelectBox,
    TextInput,
    Button,
    Header,
    Switch,
    Text
} from "ncore-ui-kit";
import {
    useNavigation
} from "@react-navigation/native";
import type RootStackParamList from "../../../navigation/type";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";
import type {
    IButtonProps
} from "ncore-ui-kit";
import {
    Home as HomeIcon
} from "lucide-react-native";

const BUTTON_TYPES: Array<IButtonProps["type"]> = [
    "primary",
    "danger",
    "warning",
    "success",
    "info"
];
const BUTTON_VARIANTS: Array<IButtonProps["variant"]> = [
    "filled",
    "outline",
    "ghost"
];
const BUTTON_SIZES: Array<IButtonProps["size"]> = [
    "small",
    "medium",
    "large"
];

const BUTTON_TYPES_DATA = BUTTON_TYPES.map(item => ({
    __title: item as string,
    __key: item as string
}));

const BUTTON_SPREAD_BEHAVIOURS: Array<IButtonProps["spreadBehaviour"]> = [
    "baseline",
    "stretch",
    "free"
];

const BUTTON_VARIANTS_DATA = BUTTON_VARIANTS.map(item => ({
    __title: item as string,
    __key: item as string
}));

const BUTTON_SIZES_DATA = BUTTON_SIZES.map(item => ({
    __title: item as string,
    __key: item as string
}));

const ButtonPage = () => {
    const {
        radiuses,
        borders,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    const [
        typeIndex,
        setTypeIndex
    ] = useState<number>(0);

    const [
        spreadIndex,
        setSpreadIndex
    ] = useState<number>(0);

    const [
        variantIndex,
        setVariantIndex
    ] = useState<number>(0);

    const [
        sizeIndex,
        setSizeIndex
    ] = useState<number>(1);

    const [
        title,
        setTitle
    ] = useState<string>("");

    const [
        isLoading,
        setIsLoading
    ] = useState<boolean>(false);

    const [
        isDisabled,
        setIsDisabled
    ] = useState<boolean>(false);

    const [
        isCustomPadding,
        setIsCustomPadding
    ] = useState<boolean>(false);

    const [
        showIcon,
        setShowIcon
    ] = useState<boolean>(false);

    const [
        iconDirection,
        setIconDirection
    ] = useState<"left" | "right">("left");

    useLayoutEffect(() => {
        navigation.setOptions({
            header: () => <Header
                isWrapSafeareaContext={false}
                title={localize("button")}
                navigation={navigation}
                isGoBackEnable={true}
            />,
            headerShown: true
        });
    }, []);

    return <PageContainer
        scrollViewProps={{
            contentContainerStyle: stylesheet.scrollContent
        }}
        isWorkWithHeaderSpace={false}
        scrollViewStyle={[
            stylesheet.container
        ]}
        isScrollable={true}
    >
        <View
            style={[
                stylesheet.contentContainer
            ]}
        >
            <Text
                style={stylesheet.descText}
                variant="bodyMediumSize"
                color="mid"
            >
                {localize("button-desc")}
            </Text>
            <View
                style={[
                    stylesheet.previewContainer,
                    {
                        borderColor: colors.content.border.subtle,
                        marginBottom: spaces.spacingMd,
                        borderRadius: radiuses.form,
                        borderWidth: borders.line,
                        padding: spaces.spacingMd
                    }
                ]}
            >
                <Button
                    spreadBehaviour={BUTTON_SPREAD_BEHAVIOURS[spreadIndex]}
                    title={title || localize("example-button")}
                    variant={BUTTON_VARIANTS[variantIndex]}
                    icon={showIcon ? ({
                        color,
                        size
                    }) => <HomeIcon
                        color={colors.content.icon[color]}
                        size={size}
                    /> : undefined}
                    style={{
                        marginBottom: spaces.spacingLg
                    }}
                    isCustomPadding={isCustomPadding}
                    type={BUTTON_TYPES[typeIndex]}
                    size={BUTTON_SIZES[sizeIndex]}
                    iconDirection={iconDirection}
                    isDisabled={isDisabled}
                    isLoading={isLoading}
                    onPress={() => {
                    }}
                />
            </View>
            <View
                style={{
                    marginBottom: spaces.spacingMd
                }}
            >
                <Text
                    style={{
                        marginBottom: spaces.spacingSm
                    }}
                    variant="headlineSmallSize"
                >
                    {localize("usage")}
                </Text>
                <CodeViewer
                    code={"import {\n    Button\n} from \"ncore-ui-kit\";\n\n<Button\n    title=\"Click Me!\"\n    onPress={() => console.log(\"Pressed\")}\n    spreadBehaviour=\"stretch\"\n    iconDirection=\"left\"\n    variant=\"filled\"\n    type=\"primary\"\n    size=\"medium\"\n/>"}
                    language="tsx"
                />
            </View>
            <TextInput
                placeholder={localize("enter-text")}
                style={{
                    marginBottom: spaces.spacingMd
                }}
                title={localize("button-title")}
                spreadBehaviour="stretch"
                onChangeText={setTitle}
                value={title}
            />
            <View
                style={[
                    stylesheet.rowContainer,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <SelectBox
                    onChange={(selectedItems) => {
                        if (selectedItems.length > 0) {
                            const index = BUTTON_TYPES.indexOf(selectedItems[0]!.__key as IButtonProps["type"]);
                            if (index !== -1) setTypeIndex(index);
                        }
                    }}
                    initialSelectedItems={

                        BUTTON_TYPES_DATA[typeIndex]

                            ? [
                                BUTTON_TYPES_DATA[typeIndex]!
                            ]

                            : []

                    }
                    titleExtractor={(item) => item.__title}
                    keyExtractor={(item) => item.__key}
                    title={localize("change-type")}
                    data={BUTTON_TYPES_DATA}
                    spreadBehaviour="free"
                />
                <SelectBox
                    onChange={(selectedItems) => {
                        if (selectedItems.length > 0) {
                            const index = BUTTON_VARIANTS.indexOf(selectedItems[0]!.__key as IButtonProps["variant"]);
                            if (index !== -1) setVariantIndex(index);
                        }
                    }}
                    initialSelectedItems={

                        BUTTON_VARIANTS_DATA[variantIndex]

                            ? [
                                BUTTON_VARIANTS_DATA[variantIndex]!
                            ]

                            : []

                    }
                    titleExtractor={(item) => item.__title}
                    keyExtractor={(item) => item.__key}
                    title={localize("change-variant")}
                    data={BUTTON_VARIANTS_DATA}
                    spreadBehaviour="free"
                />
                <Button
                    onPress={() => {
                        setSpreadIndex(prev => prev + 1 > BUTTON_SPREAD_BEHAVIOURS.length - 1 ? 0 : prev + 1);
                    }}
                    title={`${localize("change-spread")}: ${BUTTON_SPREAD_BEHAVIOURS[spreadIndex]}`}
                    verticalLocationForStretchFix="center"
                    isFixVerticalStretch={true}
                    spreadBehaviour="free"
                />
                <SelectBox
                    onChange={(selectedItems) => {
                        if (selectedItems.length > 0) {
                            const index = BUTTON_SIZES.indexOf(selectedItems[0]!.__key as IButtonProps["size"]);
                            if (index !== -1) setSizeIndex(index);
                        }
                    }}
                    initialSelectedItems={

                        BUTTON_SIZES_DATA[sizeIndex]

                            ? [
                                BUTTON_SIZES_DATA[sizeIndex]!
                            ]

                            : []

                    }
                    titleExtractor={(item) => item.__title}
                    keyExtractor={(item) => item.__key}
                    title={localize("change-size")}
                    data={BUTTON_SIZES_DATA}
                    spreadBehaviour="free"
                />
                <View
                    style={stylesheet.switchesContainer}
                >
                    <Switch
                        onPress={() => {
                            setIconDirection(iconDirection === "left" ? "right" : "left");
                        }}
                        title={localize("toggle-icon-direction")}
                        isActive={iconDirection === "right"}
                        subTitle={localize(iconDirection)}
                        spreadBehaviour="free"
                    />
                    <Switch
                        title={localize("toggle-icon")}
                        onPress={() => {
                            setShowIcon(!showIcon);
                        }}
                        spreadBehaviour="free"
                        isActive={showIcon}
                    />
                    <Switch
                        title={localize("toggle-loading")}
                        onPress={() => {
                            setIsLoading(!isLoading);
                        }}
                        spreadBehaviour="free"
                        isActive={isLoading}
                    />
                    <Switch
                        title={localize("toggle-disabled")}
                        onPress={() => {
                            setIsDisabled(!isDisabled);
                        }}
                        spreadBehaviour="free"
                        isActive={isDisabled}
                    />
                    <Switch
                        onPress={() => {
                            setIsCustomPadding(!isCustomPadding);
                        }}
                        title={localize("toggle-custom-padding")}
                        isActive={isCustomPadding}
                        spreadBehaviour="free"
                    />
                </View>
            </View>
        </View>
    </PageContainer>;
};
export default ButtonPage;
